/* 
BoxCheese.js is a custom component that displays the details of a cheese record in a box format. It is used in the ProvenanceList.js component to display the details of a cheese record. 
*/
import React from 'react';
import { useTheme, Typography, Chip, Box, Hidden } from '@mui/material';
import { TextInput, SelectInput } from 'react-admin';
import { required } from 'ra-core';

/**
 * Cheese product box component
 * @component
 * @example
 * // Example record structure:
 * const cheeseRecord = {
 *   id: "C123",
 *   name: "Aged Parmigiano Reggiano",
 *   producer: "Consorzio del Formaggio",
 *   origin: "Parma, Italy",
 *   ageInMonths: 24,
 *   milkType: "Cow",
 *   texture: "Hard",
 *   classification: "DOP",
 *   batchNumber: "BN456",
 *   sku: "SKU789",
 *   description: "Aged for 24 months, rich in flavor...",
 *   image: "url/to/cheese/image.jpg"
 * };
 */

export const defaultCheeseImage = "https://placehold.co/200x300?text=Cheese&font=playfair-display";

const BoxCheese = ({ record }) => {
    const theme = useTheme();
    return (
        <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                {record.name || 'Unnamed Cheese'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Producer:</strong> {record?.producer || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Origin:</strong> {record?.region || 'Unknown'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip label={record?.aging ? `${record.aging} months` : 'N/A'} size="small" color="primary" variant="outlined" />
                <Chip label={record?.milkType || 'N/A'} size="small" color="secondary" variant="outlined" />
                <Chip label={record?.texture || 'N/A'} size="small" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Typography variant="body2"><strong>Product ID:</strong> {record.id || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Batch Number:</strong> {record?.batchNumber || 'N/A'}</Typography>
                <Typography variant="body2"><strong>SKU:</strong> {record?.sku || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <strong>Description:</strong> {record?.description || 'N/A'}
                </Typography>
            </Box>
        </Box>
    );
};

export const renderCheeseFields = () => (
    <>
      
      <TextInput label="Cheese" source="data.name" validate={required()} />
      <TextInput label="Batch #" source="data.batchNumber" validate={required()} />
      <TextInput label="SKU" source="data.sku" validate={required()} />
      <TextInput label="Price per unit" source="data.price" validate={required()} />
      <TextInput label="Producer" source="data.producer" />
      <TextInput label="Region" source="data.region" />
      <SelectInput label="Milk Type" source="data.milkType" choices={[
        { id: 'Cow', name: 'Cow' },
        { id: 'Goat', name: 'Goat' },
        { id: 'Sheep', name: 'Sheep' },
        { id: 'Buffalo', name: 'Buffalo' },
        { id: 'Mixed', name: 'Mixed' },
      ]} />
      <SelectInput label="Texture" source="data.texture" choices={[
        { id: 'Soft', name: 'Soft' },
        { id: 'Semi-soft', name: 'Semi-soft' },
        { id: 'Semi-hard', name: 'Semi-hard' },
        { id: 'Hard', name: 'Hard' },
        { id: 'Crumbly', name: 'Crumbly' },
      ]} />
      <SelectInput label="Aging" source="data.aging" choices={[
        { id: 'Fresh', name: 'Fresh' },
        { id: 'Short (1-3 months)', name: 'Short (1-3 months)' },
        { id: 'Medium (3-6 months)', name: 'Medium (3-6 months)' },
        { id: 'Long (6+ months)', name: 'Long (6+ months)' },
      ]} />
      <TextInput label="Description" multiline source="data.description" />
    </>
);

export default BoxCheese;